import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './GSSharesPopup.css';
import { apiGet } from '../../utils/api';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';
import {
  RANGE_OPTIONS,
  periodKpiLabel,
  computePerf,
  hasEnoughHistory,
  filterHistoryToRange,
  computeYDomain,
} from './popupShared';

const InitialTaxRate = 48.5;

const parseShareDate = (dateStr) => {
  const months = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
  const [day, mon, year] = dateStr.split('-');
  return new Date(parseInt(year), months[mon], parseInt(day));
};

// ─── Unified-header helpers ────────────────────────────────────────────────────

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function gsPnlClass(value) {
  if (value == null || isNaN(value)) return '';
  return value >= 0 ? 'gs-pos' : 'gs-neg';
}

function gsFormatYAxis(value) {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000)     return `€${(value / 1_000).toFixed(0)}k`;
  return `€${value}`;
}

function gsFormatXAxis(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function GSChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const dateLabel = new Date(label).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <div className="gs-chart-tooltip">
      <div className="gs-chart-tooltip-date">{dateLabel}</div>
      <div>€{Math.round(payload[0]?.value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function GSSharesPopup({ onClose, gsShares, investment }) {
  const [currentStockPrice, setCurrentStockPrice] = useState(null);
  const [eurUsdRate, setEurUsdRate] = useState(null);
  const [taxRate, setTaxRate] = useState(InitialTaxRate);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Unified header state (chart + period KPI) ──
  const [range, setRange] = useState('1W');

  const valueHistory = useMemo(
    () => investment?.valueHistory || [],
    [investment]
  );
  const selectedOption = useMemo(
    () => RANGE_OPTIONS.find(r => r.label === range) || RANGE_OPTIONS[1],
    [range]
  );
  const chartData = useMemo(
    () => filterHistoryToRange(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );
  const yDomain = useMemo(() => computeYDomain(chartData), [chartData]);
  const periodPerf = useMemo(
    () => computePerf(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );
  const dailyPerf = useMemo(
    () => computePerf(valueHistory, RANGE_OPTIONS[0]),
    [valueHistory]
  );
  const portfolioEur = investment?.eurValue ?? null;

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiGet('/api/GSShares/market-data');
        
        if (response.success) {
          setCurrentStockPrice(response.data.stockPrice);
          setEurUsdRate(response.data.fxRate);
        } else {
          throw new Error(response.message || 'Failed to fetch market data');
        }
      } catch (error) {
        console.error('Error fetching market data:', error);
        setError('Failed to load market data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, []);

  const getCurrentShares = () => {
    if (!Array.isArray(gsShares) || gsShares.length === 0) return {};
    const today = new Date();
    return [...gsShares]
      .filter(s => parseShareDate(s.date) <= today)
      .sort((a, b) => parseShareDate(b.date) - parseShareDate(a.date))[0] || {};
  };

  const currentShares = getCurrentShares();

  const handleTaxRateChange = (e) => {
    setTaxRate(e.target.value);
  };

  const calculateEurValue = (shares) => {
    const value = (1 - taxRate / 100) * shares * currentStockPrice / eurUsdRate;
    return value;
  };

  const formatEur = (val) =>
    val != null && !isNaN(val)
      ? `€${Math.round(val).toLocaleString()}`
      : 'N/A';

  const sortedShares = Array.isArray(gsShares)
    ? [...gsShares].sort((a, b) => parseShareDate(a.date) - parseShareDate(b.date))
    : [];

  let previousPostTaxVesting = 0;
  let previousPostTaxDistribution = 0;
  let previousPostTaxTransferable = 0;

  if (error) {
    return (
      <div className="popup-overlay" onClick={onClose}>
        <div className="popup-content" onClick={(e) => e.stopPropagation()}>
          <h2>GS Shares Overview</h2>
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content gs-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="gs-popup-header">
          <h2>Fidelity GS — Portfolio</h2>
          <div className="popup-header-actions">
            <SeeFilesButton url={getDriveFolderUrl('Fidelity GS')} />
            <button className="gs-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* ── Account summary bar ── */}
        {investment && (
          <div className="gs-account-bar">
            <div className="gs-account-total">
              <span className="gs-label">Portfolio Value</span>
              <span className="gs-big-value">
                €{Math.round(portfolioEur || 0).toLocaleString()}
              </span>
            </div>
            <div className="gs-account-stat">
              <span className="gs-label">Today</span>
              <span className={`gs-pnl ${gsPnlClass(dailyPerf?.abs)}`}>
                {dailyPerf ? fmtEur(dailyPerf.abs, true) : '—'}
                {dailyPerf?.pct != null && (
                  <span className="gs-pct"> ({fmtPct(dailyPerf.pct)})</span>
                )}
              </span>
            </div>
            <div className="gs-account-stat">
              <span className="gs-label">{periodKpiLabel(selectedOption)}</span>
              <span className={`gs-pnl ${gsPnlClass(periodPerf?.abs)}`}>
                {periodPerf ? fmtEur(periodPerf.abs, true) : '—'}
                {periodPerf?.pct != null && (
                  <span className="gs-pct"> ({fmtPct(periodPerf.pct)})</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* ── Performance chart ── */}
        {investment && (
          <div className="gs-chart-section">
            <div className="gs-chart-header">
              <span className="gs-section-title">Account Performance</span>
              <div className="gs-range-buttons">
                {RANGE_OPTIONS.map(r => {
                  const enabled = hasEnoughHistory(valueHistory, r);
                  return (
                    <button
                      key={r.label}
                      className={`gs-range-btn ${range === r.label ? 'active' : ''}`}
                      onClick={() => enabled && setRange(r.label)}
                      disabled={!enabled}
                      title={enabled ? '' : 'Not enough history for this range yet'}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tickFormatter={gsFormatXAxis} tick={{ fontSize: 11 }} minTickGap={40} />
                  <YAxis tickFormatter={gsFormatYAxis} tick={{ fontSize: 11 }} width={72} domain={yDomain} allowDataOverflow={false} />
                  <Tooltip content={<GSChartTooltip />} />
                  <Line type="monotone" dataKey="eurValue" stroke="#8B5CF6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="gs-chart-empty">Not enough history for this range yet.</div>
            )}
          </div>
        )}

        <div className="stock-price-section">
          <div className="stock-price-block">
            <p>Current Stock Price: {loading ? 'Loading...' : `$${currentStockPrice}`}</p>
          </div>
          <div className="fx-rate-block">
            <p>EURUSD: {loading ? 'Loading...' : eurUsdRate}</p>
          </div>
        </div>
        <div className="tax-rate-block">
          <p>Tax Rate: <input type="number" value={taxRate} onChange={handleTaxRateChange} />%</p>
        </div>
        <div className="overview">
          {(() => {
            const v  = currentShares.vesting;
            const d  = currentShares.distribution;
            const t  = currentShares.transferable;
            const net = (1 - taxRate / 100);
            const postTaxVested      = v ? Math.round(v * net) : null;
            const postTaxDistributed = d ? Math.round(d * net) : null;
            const pendingDelivery    = (v && d) ? Math.round((v - d) * net) : null;
            const ready = !loading && currentStockPrice && eurUsdRate;
            return (
              <table className="overview-table">
                <thead>
                  <tr><th>Category</th><th>Pre-tax shares</th><th>Post-tax shares</th><th>EUR value</th></tr>
                </thead>
                <tbody>
                  <tr className="overview-row--highlight">
                    <td><strong>Vested (total)</strong><br/><span className="overview-note">All RSUs vested to date</span></td>
                    <td>{v ?? 'N/A'}</td>
                    <td>{postTaxVested ?? 'N/A'}</td>
                    <td>{ready && v ? formatEur(calculateEurValue(v)) : '…'}</td>
                  </tr>
                  <tr>
                    <td>Distributed to Fidelity<br/><span className="overview-note">Delivered to your account</span></td>
                    <td>{d ?? 'N/A'}</td>
                    <td>{postTaxDistributed ?? 'N/A'}</td>
                    <td>{ready && d ? formatEur(calculateEurValue(d)) : '…'}</td>
                  </tr>
                  <tr>
                    <td>Vested, pending delivery<br/><span className="overview-note">Vested but not yet in account</span></td>
                    <td>{(v && d) ? (v - d) : 'N/A'}</td>
                    <td>{pendingDelivery ?? 'N/A'}</td>
                    <td>{ready && v && d ? formatEur(calculateEurValue(v - d)) : '…'}</td>
                  </tr>
                  <tr>
                    <td>Transferable<br/><span className="overview-note">No transfer restrictions</span></td>
                    <td>{t ?? 'N/A'}</td>
                    <td>{t ? Math.round(t * net) : 'N/A'}</td>
                    <td>{ready && t ? formatEur(calculateEurValue(t)) : '…'}</td>
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Vesting</th>
              <th>Distribution</th>
              <th>Transferable</th>
              <th>Post Tax Vesting</th>
              <th>Post Tax Distribution</th>
              <th>Post Tax Transferable</th>
            </tr>
          </thead>
          <tbody>
            {sortedShares.length > 0 ? sortedShares.map((share, index) => {
              const previousShare = index > 0 ? sortedShares[index - 1] : null;
              const postTaxVesting = previousShare
                ? (previousPostTaxVesting + ((share.vesting - previousShare.vesting) * (1 - taxRate / 100) * currentStockPrice/ eurUsdRate))
                : calculateEurValue(share.vesting);
              previousPostTaxVesting = parseFloat(postTaxVesting);

              const postTaxDistribution = previousShare
                ? (previousPostTaxDistribution + ((share.distribution - previousShare.distribution) * (1 - taxRate / 100) * currentStockPrice/ eurUsdRate))
                : calculateEurValue(share.distribution);
              previousPostTaxDistribution = parseFloat(postTaxDistribution);

              const postTaxTransferable = previousShare
                ? (previousPostTaxTransferable + ((share.transferable - previousShare.transferable) * (1 - taxRate / 100) * currentStockPrice/ eurUsdRate))
                : calculateEurValue(share.transferable);
              previousPostTaxTransferable = parseFloat(postTaxTransferable);

              return (
                <tr key={share._id}>
                  <td>{share.date}</td>
                  <td>{share.vesting}</td>
                  <td>{share.distribution}</td>
                  <td>{share.transferable}</td>
                  <td>{loading ? 'Loading...' : postTaxVesting.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  <td>{loading ? 'Loading...' : postTaxDistribution.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  <td>{loading ? 'Loading...' : postTaxTransferable.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="7">No GS shares data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GSSharesPopup;